<script lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { defineAsyncComponent } from '@@/bits/vue'
import { WallWishGroupByTypes } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Id, WallGroupPost } from '@@/types'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfacePostDraggable from '@@/vuecomponents/SurfacePostDraggable.vue'
import SurfacePostGroupHeaderSection from '@@/vuecomponents/SurfacePostGroupHeaderSection.vue'
import SurfacePostGroupHeaderSectionOld from '@@/vuecomponents/SurfacePostGroupHeaderSectionOld.vue'
import SurfacePostsStreamSectionHeaderDragPreview from '@@/vuecomponents/SurfacePostsStreamSectionHeaderDragPreview.vue'
import { useSurfaceSectionDroppableElement } from '@@/vuecomposables/surface_section_droppable_element'
import { useSurfaceSectionHeaderDraggableElement } from '@@/vuecomposables/surface_section_header_draggable_element'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const OzDropIndicatorLine = defineAsyncComponent(() => import('@@/library/v4/components/OzDropIndicatorLine.vue'))

export const SURFACE_STREAM_POST_DRAGGABLE_TYPE = 'surface_stream_post'
export const SURFACE_STREAM_SECTION_HEADER_DRAGGABLE_TYPE = 'surface_stream_section_header'
export default {}
</script>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    group: WallGroupPost
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const surfacePostsStore = useSurfacePostsStore()
const surfacePostsDragAndDropStore = useSurfacePostsDragAndDropStore()
const { canIDragSections } = storeToRefs(useSurfacePermissionsStore())
const { backgroundLuminance, isPostSizeStandard, isPostSizeWide, isScreenshotMode } = storeToRefs(useSurfaceStore())
const { isAPostDragging, groupIdUnderPostBeingDragged, shouldDisableDraggingPost } =
  storeToRefs(surfacePostsDragAndDropStore)
const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
const { wishGroupBy, isGroupByNone } = storeToRefs(useSurfaceWishArrangementStore())
const windowSizeStore = useWindowSizeStore()
const { isSmallerThanTabletPortrait, windowHeight, windowWidth } = storeToRefs(windowSizeStore)

const { shouldDisplayPost } = surfacePostsStore
const { canShowDraggingOverlayOnGroup } = surfacePostsDragAndDropStore

const overlayHeight = computed(() => {
  const hoveredSectionQuery = `#section-${groupIdUnderPostBeingDragged.value}`
  const hoveredSectionElementHeight = document.querySelector(hoveredSectionQuery)?.getBoundingClientRect().height
  // add extra height to the overlay so it extends beyond posts and section header
  return hoveredSectionElementHeight ? hoveredSectionElementHeight + 32 : 0
})

const overlayWidth = computed(() => {
  const postElementWidth = document.querySelector('.wish-v2')?.getBoundingClientRect().width
  // add extra width to the overlay so it extends beyond posts
  return postElementWidth ? postElementWidth + 16 : 0
})

const minHeight = computed(() => {
  // Set minHeight as 1/4 of windowHeight since we assume that each post fills 1/4 of the screen's height
  return windowHeight.value / 4
})
const minWidth = computed(() => {
  const minimumPostWidthStandard = 548
  const minimumPostWidthWide = 748
  return isPostSizeStandard.value ? minimumPostWidthStandard : minimumPostWidthWide
})

// #region Drag and drop for section header
const { isAGroupDragging } = storeToRefs(useSurfacePostGroupByStore())
const groupHeaderSectionRef = ref<
  InstanceType<typeof SurfacePostGroupHeaderSection> | InstanceType<typeof SurfacePostGroupHeaderSectionOld>
>()
const sectionTitleRef = computed<HTMLElement | undefined>(() => {
  if (groupHeaderSectionRef.value === undefined) {
    return undefined
  }
  return groupHeaderSectionRef.value?.sectionTitleRef?.$el as HTMLElement
})
const { groupIdBeingDragged, closestEdgeToPointerForSectionHeader } = useSurfaceSectionHeaderDraggableElement({
  sectionTitleRef,
  group: props.group,
  type: SURFACE_STREAM_SECTION_HEADER_DRAGGABLE_TYPE,
  dragPreview: SurfacePostsStreamSectionHeaderDragPreview,
  dragPreviewComponentProps: computed(() => ({
    section: props.group,
  })),
  axis: 'vertical',
  isDraggable: canIDragSections,
  canDrop: canIDragSections,
})
const isCurrentSectionBeingDragged = computed(() => groupIdBeingDragged.value === props.group.id)
// #endregion

// #region Drag and drop for posts into section
const sectionElementRef = ref<HTMLElement | undefined>(undefined)
const { isDraggingPostIntoEmptySection } = useSurfaceSectionDroppableElement({
  sectionElementRef: computed(() => sectionElementRef.value),
  group: props.group,
  type: SURFACE_STREAM_POST_DRAGGABLE_TYPE,
  axis: 'vertical',
  canDrop: computed(() => !shouldDisableDraggingPost.value), // If the user can't drag posts, they can't drop them either
})
// #endregion

// #region Drag preview width
const dragPreviewWidth = computed(() => {
  const padding = 16
  if (isSmallerThanTabletPortrait.value) return windowWidth.value - padding
  // Standard post width for stream
  const standardPostWidth = 540
  if (isPostSizeStandard.value) return standardPostWidth
  // Wide post width for stream
  const widePostWidth = 748
  return widePostWidth
})
const closestEdgeToPointerForSection = computed(() => {
  if (isDraggingPostIntoEmptySection.value) return 'bottom'
  return undefined
})
// #endregion
</script>

<template>
  <section
    :id="'section-' + group.id"
    ref="sectionElementRef"
    :key="group.id"
    :data-id="group.id"
    :data-rank="group.sort_index"
    :class="[
      'flex',
      'flex-col',
      'w-full',
      'items-start tablet-portrait:items-center',
      {
        'surface-section': wishGroupBy === WallWishGroupByTypes.Section,
      },
      'relative',
    ]"
  >
    <!-- DRAG AND DROP OVERLAY -->
    <div
      v-if="canShowDraggingOverlayOnGroup(group.id)"
      :class="[
        'absolute -mt-4 z-underneath',
        'border-solid border-2 rounded-xl shadow-elevation-2',
        'pointer-events-none',
        {
          'border-grape-500 bg-modal-overlay-grape-alpha-20': backgroundLuminance === 'light',
          'border-canary-500 bg-modal-overlay-canary-alpha-20': backgroundLuminance === 'dark',
        },
      ]"
      :style="{ height: `${overlayHeight}px`, width: isSmallerThanTabletPortrait ? '100%' : `${overlayWidth}px` }"
    />
    <!-- GROUP HEADER -->
    <SurfacePostGroupHeaderSection
      v-if="isAppUsing('groupPostsV2') && !isGroupByNone"
      ref="groupHeaderSectionRef"
      :section="group"
      :class="[
        // By design, this header max width is post width - 6px.
        // Since the parent sets align-items: center, we use
        // negative margin here to pull the header a bit to the
        // left (right in RTL).
        'tablet-portrait:-ms-1.5',
        'w-full',
        // On mobile viewport, the header extends all the way to
        // the left (right in RTL) so we need to subtract 8px
        // (equal to padding).
        isPostSizeStandard && 'tablet-portrait:max-w-[calc(540px-6px)] max-w-[calc(100%-6px-8px)]',
        isPostSizeWide && 'tablet-portrait:max-w-[calc(748px-6px)] max-w-[calc(100%-6px-24px)]',
        isCurrentSectionBeingDragged && 'opacity-50',
      ]"
      :hide-buttons="isAGroupDragging"
    />
    <SurfacePostGroupHeaderSectionOld
      v-if="!isAppUsing('groupPostsV2') && wishGroupBy === WallWishGroupByTypes.Section"
      ref="groupHeaderSectionRef"
      :section="group"
      :class="[
        // By design, this header max width is post width - 6px.
        // Since the parent sets align-items: center, we use
        // negative margin here to pull the header a bit to the
        // left (right in RTL).
        'tablet-portrait:-ms-1.5',
        'w-full',
        // On mobile viewport, the header extends all the way to
        // the left (right in RTL) so we need to subtract 8px
        // (equal to padding).
        isPostSizeStandard && 'tablet-portrait:max-w-[calc(540px-6px)] max-w-[calc(100%-6px-8px)]',
        isPostSizeWide && 'tablet-portrait:max-w-[calc(748px-6px)] max-w-[calc(100%-6px-24px)]',
        isCurrentSectionBeingDragged && 'opacity-50',
      ]"
      :hide-buttons="isAGroupDragging"
    />

    <OzDropIndicatorLine
      v-if="closestEdgeToPointerForSectionHeader || closestEdgeToPointerForSection"
      :edge="closestEdgeToPointerForSectionHeader || closestEdgeToPointerForSection"
      :gap="40"
      :class="[
        'my-1',
        isPostSizeStandard && 'tablet-portrait:max-w-[calc(540px-6px)] max-w-[calc(100%-6px-8px)]',
        isPostSizeWide && 'tablet-portrait:max-w-[calc(748px-6px)] max-w-[calc(100%-6px-24px)]',
        'mx-auto',
      ]"
    />

    <!-- POSTS IN GROUP -->
    <div
      v-show="!isAGroupDragging"
      :id="`group-posts-${group.id}`"
      :data-id="group.id"
      :class="[
        'flex',
        'flex-col',
        'items-center',
        'space-y-2 tablet-portrait:space-y-4',
        'box-border',
        group.id === WallWishGroupByTypes.None ? 'mt-0' : 'mt-4',
        isPostSizeStandard && 'px-2 tablet-portrait:px-0',
        isPostSizeWide && 'px-2 tablet-portrait:px-6',
        'w-full',
        'group-posts',
      ]"
    >
      <template v-for="post in allSortedPostsByGroup[group.id]" :key="post.cid">
        <SurfaceLazy
          v-show="shouldDisplayPost(post.cid)"
          :data-cid="post.cid"
          :data-id="post.id"
          :min-height="minHeight"
          :min-width="minWidth"
          data-testid="postWrapper"
          :class="['wish-wrapper', 'w-full', 'flex', 'justify-center']"
          :render-immediately="isScreenshotMode"
        >
          <SurfacePostDraggable
            v-if="post.id"
            :class="[
              // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/stream.scss
              'wish-v2',
              'w-full',
              isPostSizeStandard && 'tablet-portrait:max-w-135',
              isPostSizeWide && 'tablet-portrait:max-w-187',
            ]"
            :index="post.id"
            :post="post"
            :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] || [] : []"
            :post-comment-ids="post.id ? commentIdsByPostId[post.id] || [] : []"
            :drag-element-type="SURFACE_STREAM_POST_DRAGGABLE_TYPE"
            :drop-element-type="SURFACE_STREAM_POST_DRAGGABLE_TYPE"
            axis="vertical"
            :gap="16"
            :should-compress="isAPostDragging"
            :is-draggable="!shouldDisableDraggingPost"
            :drag-preview-width="dragPreviewWidth"
            :group-id="group.id"
          />
        </SurfaceLazy>
      </template>
    </div>
  </section>
</template>
